import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ValidateDiscountsResponse,
  DiscountValidationResult,
} from '@erp/rpc';

interface UseValidateSalesOrderDraftDiscountsParams {
  salesOrderDraftId: string;
  /** Enable the query (defaults to true) */
  enabled?: boolean;
}

/**
 * Hook to validate discounts for a sales order draft's line items.
 * Uses POST but is a read operation (discount/price check).
 *
 * - If a discount exists, compares PO price to the discounted price
 * - If no discount exists, compares PO price to Oracle EBS list price
 *
 * Returns a map of productVariantId -> DiscountValidationResult.
 */
function useValidateSalesOrderDraftDiscounts({
  salesOrderDraftId,
  enabled = true,
}: UseValidateSalesOrderDraftDiscountsParams) {
  const queryEnabled = enabled && !!salesOrderDraftId;

  const query = useQuery({
    ...orpc.accountsReceivable.salesOrderDrafts.validateDiscounts.queryOptions({
      input: { stagedPurchaseOrderId: salesOrderDraftId },
    }),
    enabled: queryEnabled,
    // Don't refetch on window focus for this potentially slow query
    refetchOnWindowFocus: false,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
  });

  const results = query.data?.results ?? {};

  // React Query v5 can briefly have status='pending' with isFetching=false
  // during initialization. Use isPendingOrLoading to ensure loading state
  // is shown until data actually arrives.
  const isPendingOrLoading = query.isLoading || query.status === 'pending';

  return {
    ...query,
    isPendingOrLoading,
    results,
  } as typeof query & {
    data?: ValidateDiscountsResponse;
    results: Record<string, DiscountValidationResult>;
    isPendingOrLoading: boolean;
  };
}

export default useValidateSalesOrderDraftDiscounts;
